import {Component, ElementRef, OnInit, ViewChild, Input, LOCALE_ID, Inject} from '@angular/core';

import {PlotsService} from "../../services/plots.service";
import {PlotEvent, PlotMonitors} from "../../model/plot";
import {firstValueFrom} from "rxjs";
import Chart, {LegendItem} from "chart.js/auto";
import 'chartjs-adapter-moment';
import annotationPlugin, {AnnotationOptions, LabelAnnotationOptions} from 'chartjs-plugin-annotation';
import {dashboardResized, eventsUpdate} from '../../events';
import {CommonModule} from "@angular/common";
import {Monitor} from "../../model/assessment";
import {selectedPlotsChange, selectedPhotoChange} from '../../events';
import {UtilService} from '../../services/util.service';
import {ChartService, ChartObject} from '../../services/chart.service';
import {TSDataSorted, Coherence} from '../../model/plot';
import {colorCodes} from '../../constants';

Chart.register(annotationPlugin);

@Component({
    selector: 'app-chart',
    templateUrl: './chart.component.html',
    imports: [
        CommonModule
    ],
    styleUrls: ['./chart.component.scss']
})
export class ChartComponent implements OnInit {
  @Input() userIsCustomer: boolean = false;

  @ViewChild('canvas') canvas: ElementRef | undefined;

  plotIds: number[] = [];
  chart: Chart | undefined;
  dataAvailable = false;

  events: PlotEvent[] = [];
  activePhotoDate: Date | undefined;
  activePhotoSource: string | undefined;

  constructor(private plotsService: PlotsService,
    private util: UtilService,
    private chartService: ChartService,
    @Inject(LOCALE_ID) private locale: string
  ) {
  }

  ngOnInit() {
    dashboardResized
      .subscribe(() => {
        try {
          console.log('on dashboard resize');
          this.chart?.resize();
        } catch (e) {
          // nobody cares
        }
      });
    selectedPlotsChange
      .subscribe(async data => {
        console.log('on selected plot change', data.plotIds);
        this.plotIds = data.plotIds;
        if (data.plotIds.length >= 1) {
          await this.getData(data.plotIds[0]);
        } else {
          await this.clearChart();
        }
      });
    eventsUpdate.subscribe(async data => {
      this.events = this.events.map(event => {
        if (event.date.toISOString().substring(0, 10) === data.event.date && event.source === data.event.source) {
          return {
            ...event,
            valid: data.event.valid
          };
        }
        return event;
      })

      await this.updateChartEventColor(this.events.find(event => event.date.toISOString().substring(0, 10) === data.event.date && event.source === data.event.source)!);    
    });

    selectedPhotoChange.subscribe(async photoData => {
      this.activePhotoDate = photoData && photoData.date && photoData.date.length > 0 ? new Date(photoData.date) : undefined;
      this.activePhotoSource = photoData ? photoData.source : undefined;

      if (!photoData?.firstPhoto) {
        await this.updateChartPhotoDate();
      }
      
    });
  }




  private async getData(plotId: number) {
    try {
      await this.clearChart();
      const planetNDVI: ChartObject[] = [];
      const sentinelNDVI: ChartObject[] = [];
      const coherences: Coherence[] = [];
      const precipitation: ChartObject[] = [];
      const temperature: ChartObject[] = [];

      // get the data
      const allNDVI = await firstValueFrom(this.plotsService.getNdviData(plotId));
      if (allNDVI) allNDVI.forEach(entry => {
        if (entry.source === 'PLANET') {
          planetNDVI.push({ date: new Date(entry.date), value: entry.median });
        } else {
          sentinelNDVI.push({ date: new Date(entry.date), value: entry.median })
        }
      });

      const allCoherence =  await firstValueFrom(this.plotsService.getCoherenceData(plotId))
      let vhCoherence: any[] = [];
      if (allCoherence) {
        vhCoherence = allCoherence.filter(entry => entry.polarisation === 'VH');        
      
        vhCoherence.forEach(entry => {
          let coherenceCategory = coherences.find(value => value.orbit === entry.orbit && value.polarisation === entry.polarisation);
          if (!coherenceCategory) {
            coherenceCategory = {orbit: entry.orbit, polarisation: entry.polarisation as 'VH' | 'VV', values: []};
            coherences.push(coherenceCategory);
          }
          coherenceCategory.values.push({date: new Date(entry.date), value: entry.median});
        });
      }

      const orbits: string[]  = Array.from(new Set(coherences.map(coherenceCategory => coherenceCategory.orbit)));

      this.events = await firstValueFrom(this.plotsService.getPlotEvents(plotId));

      const allWeather = await firstValueFrom(this.plotsService.getWeatherData(plotId));
      if (allWeather) {
        allWeather.forEach(entry => {
          precipitation.push({ date: new Date(entry.date), value: entry.precipitation });
          temperature.push({ date: new Date(entry.date), value: entry.temperature });
        });
      }

      const {currentYear, interpolatedData} = await this.chartService.interpolateChartData(planetNDVI, sentinelNDVI, coherences, 
        precipitation, temperature);

      const lockout = await firstValueFrom(this.plotsService.getMonitor('SPERR_BRA'));
      const plotMonitors = await firstValueFrom(this.plotsService.getPlotMonitors(plotId));
      const isLockoutShown = this.findSperrBra(plotMonitors);
      if (isLockoutShown) {
        lockout.dateStartParsed = this.util.getDateParsed(lockout.dateStart, currentYear);
        lockout.dateEndParsed = this.util.getDateParsed(lockout.dateEnd, currentYear);
      }
      console.log('lockout start date parsed', lockout.dateStartParsed);

      if (interpolatedData.length > 0) {
        await this.drawChart(interpolatedData, sentinelNDVI, planetNDVI, orbits, coherences, lockout);
        this.dataAvailable = true;
      } else {
        this.dataAvailable = false;
      }

    } catch (e) {
      console.error(e);
      // showNotification.next({ title: 'Error', message: `1 No plot found for ID ${plotId}.` + e });
    }
  }



  private async drawChart(interpolatedData: TSDataSorted[], sentinelOriginal: ChartObject[],
    planetOriginal: ChartObject[], orbits: string[], coherences: Coherence[],
    lockout?: Monitor | undefined) {

    await this.clearChart();
    if (interpolatedData.length > 0 && this.canvas) {

      const chartData: { labels: any[], datasets: any[], allAnnotations: any[] } = await this.chartService.createChartData(interpolatedData, sentinelOriginal,
        planetOriginal, orbits, coherences, lockout, this.events, this.activePhotoDate, this.activePhotoSource);

      const labels = chartData.labels;
      const datasets = chartData.datasets;
      const allAnnotations = chartData.allAnnotations;

      const me = this;

      for (var name in allAnnotations) {
        if (name.indexOf('NDVI Sentinel') === 0) {
          allAnnotations[name].display = function() { 
            const dataset = me.chart?.legend?.legendItems?.find((obj: any) => {
              return obj.text === 'NDVI Sentinel';
            });
            return dataset && !dataset.hidden;
            }
        } else if (name.indexOf('NDVI Planet') === 0) {
          allAnnotations[name].display = function() { 
            const dataset = me.chart?.legend?.legendItems?.find((obj: any) => {
              return obj.text === 'NDVI Planet';
            });
            return dataset && !dataset.hidden;
            }
        } else if (name.indexOf('VH COH') === 0) {
          allAnnotations[name].display = function() { 
            const dataset = me.chart?.legend?.legendItems?.find((obj: any) => {
              return obj.text.includes('VH COH');
            });
            return dataset && !dataset.hidden;
            }
        } else if (name.indexOf('Precipitation') === 0) {
          allAnnotations[name].display = function() { 
            const dataset = me.chart?.legend?.legendItems?.find((obj: any) => {
              return obj.text.includes('Precipitation');
            });
            return dataset && !dataset.hidden;
            }
        } else if (name.indexOf('Temperature') === 0) {
          allAnnotations[name].display = function() { 
            const dataset = me.chart?.legend?.legendItems?.find((obj: any) => {
              return obj.text.includes('Temperature');
            });
            return dataset && !dataset.hidden;
            }
        } else if (name.indexOf('activePhoto') === 0) {
          const label = $localize`:@@photo:Photo`;
          allAnnotations[name].display = function() { 
            const dataset = me.chart?.legend?.legendItems?.find((obj: any) => {
              return obj.text === label;
            });
            (dataset ?? {} as LegendItem).strokeStyle = me.activePhotoSource && me.activePhotoSource.toLowerCase().indexOf('sfb') !== -1 ? colorCodes.sfb_data : colorCodes.ast_data;
            return dataset && !dataset.hidden;
            }
        }
      }

      this.chart = new Chart(this.canvas.nativeElement, {
        type: 'line',
        data: {
          labels,
          datasets
        },
        options: {
          locale: this.locale,
          animation: false,
          scales: {
            y: {
              min: 0,
              max: 1.0
            },
            y1: {
              min: -10,
              max: 100,
              position: 'right',
              grid: {
                drawOnChartArea: false, // only want the grid lines for one axis to show up
              },
            },
            x: {
              min: labels[0],
              max: labels[labels.length - 1],
              type: 'time',
              time: {
                unit: 'month',
                tooltipFormat:'DD.MM.YYYY',
                displayFormats: {
                  month: 'D.MM.YY'
                }
              },
              grid: {
                display: false
                // color: colorCodes.grid_line_start
              }
            }
          },
          plugins: {
            annotation: {
              annotations: allAnnotations
            },
            legend: {
              labels: {
              },
              onClick: this.legendClickHandler
            },
          },
        }
      });
      this.chart.update();
    }
  }

  async updateChartPhotoDate() {
    if (this.chart) {
      const id = 'activePhoto';
      if (this.activePhotoDate) {
        if (!(this.chart.config.options!.plugins!.annotation!.annotations as any)[id]) {
          ((this.chart.config.options!.plugins!.annotation!.annotations as any)[id] as AnnotationOptions) = await this.chartService.createPhotoAnnotation(this.activePhotoDate, this.activePhotoSource) as any;
        } else {
          ((this.chart.config.options!.plugins!.annotation!.annotations as any)[id] as AnnotationOptions).xMin = this.activePhotoDate.getTime();
          ((this.chart.config.options!.plugins!.annotation!.annotations as any)[id] as AnnotationOptions).xMax = this.activePhotoDate.getTime();
          ((this.chart.config.options!.plugins!.annotation!.annotations as any)[id] as AnnotationOptions).borderColor = this.activePhotoSource && this.activePhotoSource.toLowerCase().indexOf('sfb') !== -1 ? colorCodes.sfb_data : colorCodes.ast_data;
          ((this.chart.config.options!.plugins!.annotation!.annotations as any)[id].label as LabelAnnotationOptions).content = this.activePhotoDate.toLocaleDateString(this.locale, { day: "numeric", month: "numeric" });
          ((this.chart.config.options!.plugins!.annotation!.annotations as any)[id].label as LabelAnnotationOptions).backgroundColor = this.activePhotoSource && this.activePhotoSource.toLowerCase().indexOf('sfb') !== -1 ? colorCodes.sfb_data : colorCodes.ast_data;
        }
      } else {
        delete ((this.chart.config.options!.plugins!.annotation!.annotations as any)[id]);
      }
      this.chart.update();
    }
  }

  async updateChartEventColor(changedEvent: PlotEvent) {
    if (this.chart && this.events) {
      const id = 'Event ' + changedEvent.source + ' ' + changedEvent.date.getTime();
      const color = changedEvent.valid ? colorCodes.event_valid : colorCodes.event_invalid;
      const labelColor = changedEvent.valid ? colorCodes.event_label_valid : colorCodes.event_label_invalid;

      ((this.chart.config.options!.plugins!.annotation!.annotations as any)[id] as AnnotationOptions).borderColor = color;
      ((this.chart.config.options!.plugins!.annotation!.annotations as any)[id].label as LabelAnnotationOptions).backgroundColor = labelColor;
      
      this.chart.update();
    }
  }


  private legendClickHandler(e: any, legendItem: any, legend: any) {
    // the default stuff
    const index = legendItem.datasetIndex;
    const ci = legend.chart;
    if (ci.isDatasetVisible(index)) {
      ci.hide(index);
      legendItem.hidden = true;
    } else {
      ci.show(index);
      legendItem.hidden = false;
    }
    // to update the annotations
    e.chart.update();
  }

  private async clearChart() {
    this.chart?.destroy();
  }

  findSperrBra(plotMonitors: PlotMonitors) {
    if (plotMonitors && plotMonitors.finalResults.length > 0) {
      return plotMonitors.finalResults.find(item => item.monitor === 'SPERR_BRA');
    } else{
      return false;
    }
  }

}
